import React, { createContext, useContext, useState, useCallback, useEffect, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import { Button } from '../components/ui/button';

const NavigationContext = createContext();

export const useNavigationBlock = () => {
  const context = useContext(NavigationContext);
  if (!context) {
    throw new Error('useNavigationBlock must be used within NavigationBlockProvider');
  }
  return context;
};

export const NavigationBlockProvider = ({ children }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const [isBlocked, setIsBlocked] = useState(false);
  const [blockMessage, setBlockMessage] = useState('You have unsaved changes. Are you sure you want to leave?');
  const [showDialog, setShowDialog] = useState(false);
  const [pendingPath, setPendingPath] = useState(null);
  const [onConfirmLeave, setOnConfirmLeave] = useState(null);
  const isBlockedRef = useRef(false);
  const blockMessageRef = useRef('');

  // Keep refs in sync with state for use in event handlers
  useEffect(() => {
    isBlockedRef.current = isBlocked;
    blockMessageRef.current = blockMessage;
  }, [isBlocked, blockMessage]);

  // Handle browser back/forward buttons
  useEffect(() => {
    // Push a dummy state when blocking is enabled to catch back button
    const handlePopState = (e) => {
      if (isBlockedRef.current) {
        // Push state back to prevent navigation
        window.history.pushState(null, '', window.location.href);
        // Show our custom dialog
        setShowDialog(true);
        setPendingPath('__BACK__');
      }
    };

    // Add initial history state when blocked
    if (isBlocked) {
      window.history.pushState(null, '', window.location.href);
    }

    window.addEventListener('popstate', handlePopState);
    return () => {
      window.removeEventListener('popstate', handlePopState);
    };
  }, [isBlocked, location.pathname]);

  // Handle beforeunload for refresh/close tab
  useEffect(() => {
    const handleBeforeUnload = (e) => {
      if (isBlockedRef.current) {
        e.preventDefault();
        e.returnValue = '';
        return '';
      }
    };

    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, []);

  // Block navigation
  const blockNavigation = useCallback((message = 'You have unsaved changes. Are you sure you want to leave?') => {
    setIsBlocked(true);
    setBlockMessage(message);
  }, []);

  // Unblock navigation
  const unblockNavigation = useCallback(() => {
    setIsBlocked(false);
    setBlockMessage('');
  }, []);

  // Safe navigate that checks for blocks
  const safeNavigate = useCallback((path, options = {}) => {
    if (isBlocked) {
      setPendingPath(path);
      setShowDialog(true);
      if (options.onConfirm) {
        setOnConfirmLeave(() => options.onConfirm);
      }
      return false;
    }
    navigate(path);
    return true;
  }, [isBlocked, navigate]);

  // Confirm leaving
  const confirmLeave = useCallback(() => {
    setShowDialog(false);
    setIsBlocked(false);
    if (onConfirmLeave) {
      onConfirmLeave();
    }
    if (pendingPath === '__BACK__') {
      // Go back in history
      window.history.go(-2); // Go back 2 because we pushed a state
    } else if (pendingPath) {
      navigate(pendingPath);
    }
    setPendingPath(null);
    setOnConfirmLeave(null);
  }, [navigate, pendingPath, onConfirmLeave]);

  // Cancel leaving
  const cancelLeave = useCallback(() => {
    setShowDialog(false);
    setPendingPath(null);
    setOnConfirmLeave(null);
  }, []);

  return (
    <NavigationContext.Provider
      value={{
        isBlocked,
        blockNavigation,
        unblockNavigation,
        safeNavigate,
      }}
    >
      {children}
      
      {/* Global Unsaved Changes Dialog */}
      {showDialog && (
        <Dialog open={true} onOpenChange={cancelLeave}>
          <DialogContent className="max-w-md">
            <DialogHeader>
              <DialogTitle style={{ fontFamily: 'Manrope, sans-serif' }}>
                Unsaved Changes
              </DialogTitle>
              <DialogDescription>
                {blockMessage}
              </DialogDescription>
            </DialogHeader>
            <DialogFooter className="flex gap-2 mt-4">
              <Button
                variant="outline"
                className="bg-[#2C9DE1] hover:bg-[#1A7BB8] text-white border-0"
                onClick={cancelLeave}
              >
                Stay on Page
              </Button>
              <Button
                className="bg-red-600 hover:bg-red-700 text-white"
                onClick={confirmLeave}
              >
                Leave Without Saving
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      )}
    </NavigationContext.Provider>
  );
};

export default NavigationBlockProvider;
